<template>
  <v-app>
    <the-snackbar />
    <the-dialog />
    <the-app-bar />
    <v-main>
      <router-view />
    </v-main>
    <the-cookie-dialog />
    <the-in-app-browser-overlay />
    <the-footer />
  </v-app>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from "vue";
import { useTheme } from "vuetify";
import { useHead } from "@unhead/vue";
import { useI18n } from "vue-i18n";

import TheSnackbar from "@/components/core/TheSnackbar.vue";
import TheDialog from "@/components/core/TheDialog.vue";
import TheAppBar from "@/components/core/TheAppBar.vue";
import TheFooter from "@/components/core/TheFooter.vue";
import TheCookieDialog from "@/components/core/TheCookieDialog.vue";
import TheInAppBrowserOverlay from "@/components/core/TheInAppBrowserOverlay.vue";
import { WhitelabelProducer } from "./types/Producer";
import { useProducersStore } from "@/stores/producers";
import { useInAppBrowser } from "@/composables/useInAppBrowser";

// Composables
const { mergeLocaleMessage } = useI18n();
const producersStore = useProducersStore();
const theme = useTheme();
const { detect } = useInAppBrowser();

// Computed
const producer = computed<WhitelabelProducer>(() => producersStore.selected);

// Meta tags
useHead({
  title: "",
  titleTemplate: () => `%s | ${producer.value.title}`,
  link: [
    { rel: "icon", href: () => producer.value.favicon, type: "image/png" },
  ],
  meta: [
    { name: "description", content: () => [ producer.value.footer ].flat().join(" | ") },
    { name: "og:description", content: () => [ producer.value.footer ].flat().join(" | ") },
  ],
});

// Methods
const handleProducer = (producer: WhitelabelProducer): void => {
  console.log("[App.vue] - Setting producer", producer);

  if (!producer) return;

  theme.change(producer.theme);

  // i18n locale merge
  mergeLocaleMessage("pt-br", producer.locale["pt-br"]);
};

// Watchers
watch(
  producer,
  (newProducer) => {
    if (newProducer) {
      handleProducer(newProducer);
    }
  },
  { immediate: true },
);

// Lifecycle
onMounted(() => {
  if (producer.value) {
    handleProducer(producer.value);
  }

  detect();
});
</script>

<style>
#app {
  overflow-x: hidden;
}

body,
html {
  scroll-behavior: smooth;
}
</style>
